import { CompositeSceneRenderer } from '../renderer';
import { BoneCollisionBody, PhysicsFeature } from '../physics';
import { RenderObject } from '../object/RenderObject';
import { VisualPathNodes } from './VisualPathNodes';
import { VisualSpatialGrid } from './VisualSpatialGrid';
import { SkinnedSkeletonVisualizer } from './SkinnedSkeletonVisualizer';
import type { Scene } from '../scene/Scene';

export function setupSceneDebug(scene: Scene, renderer: CompositeSceneRenderer): void {
  // Navigation graph debug viz. Past `MAX_VIZ_NODES` total nodes (large tiled
  // scenes like iceland hit this easily), draw only the closest N to the
  // current camera position so we still get useful local debug data — and
  // refresh as the camera moves.
  let navState: { viz: VisualPathNodes; tick: { cancel(): void } } | undefined;
  scene.debugManager.add('navigation-graph', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      const MAX_VIZ_NODES = 5000;
      const REFRESH_FRAMES = 20;
      const viz = new VisualPathNodes(scene.ctx);
      let lastShown = -1;
      const refresh = () => {
        const nodes = scene.navigation.getAllNodes();
        if (nodes.length <= MAX_VIZ_NODES) {
          viz.setPathNodes([...nodes]);
          if (lastShown !== nodes.length) {
            console.log(`[nav-viz] showing all ${nodes.length} nodes`);
            lastShown = nodes.length;
          }
          return;
        }
        // Sort by squared XZ distance from the camera *target* — what the user
        // is looking at, not the eye behind them. For FreeViewCamera the target
        // is the orbit center (where they flew to); for AttachedCamera it's
        // the followed object's position. Both track as the user moves.
        const camPos = scene.ctx.engine.activeCamera.targetPosition;
        const cx = camPos[0] as number;
        const cz = camPos[2] as number;
        const sorted = nodes
          .map((n) => {
            const nx = (n.min[0] + n.max[0]) * 0.5;
            const nz = (n.min[2] + n.max[2]) * 0.5;
            const dx = nx - cx;
            const dz = nz - cz;
            return { n, d: dx * dx + dz * dz };
          })
          .sort((a, b) => a.d - b.d)
          .slice(0, MAX_VIZ_NODES)
          .map((e) => e.n);
        viz.setPathNodes(sorted);
        if (lastShown !== MAX_VIZ_NODES) {
          console.log(
            `[nav-viz] showing ${MAX_VIZ_NODES} of ${nodes.length} nearest to camera ` +
              `(${cx.toFixed(1)}, ${cz.toFixed(1)})`,
          );
          lastShown = MAX_VIZ_NODES;
        }
      };
      refresh();
      let counter = 0;
      const tick = scene.tickEventManager.registerFrameEvent(() => {
        if (++counter >= REFRESH_FRAMES) {
          counter = 0;
          refresh();
        }
      });
      navState = { viz, tick };
      dbg.objects = [viz];
    } else if (navState) {
      navState.tick.cancel();
      navState = undefined;
      dbg.objects = [];
    }
  });

  scene.debugManager.add('spatial-grid', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      if (!scene.spatialGrid) {
        console.warn('Spatial grid not loaded.');
        return;
      }
      let viz = new VisualSpatialGrid(scene.ctx);
      viz.setSpatialGrid(scene.spatialGrid);
      dbg.objects = [viz];
    }
  });

  let skeletonState:
    | {
        visualizers: Map<RenderObject, SkinnedSkeletonVisualizer>;
        tick: { cancel(): void };
      }
    | undefined;

  scene.debugManager.add('skeleton', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      const visualizers = new Map<RenderObject, SkinnedSkeletonVisualizer>();
      const tick = scene.tickEventManager.registerFrameEvent(() => {
        updateSkeletonOverlay(scene, visualizers);
      });
      skeletonState = { visualizers, tick };
    } else if (skeletonState) {
      skeletonState.tick.cancel();
      for (const viz of skeletonState.visualizers.values()) {
        scene.objectManager.removeObject(viz);
        viz.destroy();
      }
      skeletonState.visualizers.clear();
      dbg.objects = [];
      skeletonState = undefined;
    }
  });

  // The 'fps' debug toggle is just a visibility flag. Apps render their own
  // perf overlay UI (in playful, see PerfOverlay) by watching this option's
  // enabled state and reading renderer.deferredRenderer.renderStats.
  scene.debugManager.add('fps', false);

  // Hide all animated (skinned) render objects from the rendering pipeline.
  // Used to A/B-test the cost of skinning + skinned shadow casting without
  // having to find a camera angle that excludes the characters.
  scene.debugManager.add('hide-animated', false).stateChanged.subscribe((dbg) => {
    scene.objectManager.renderFilter = dbg.enabled
      ? (obj) => !(obj instanceof RenderObject && obj.isAnimated)
      : null;
  });

  scene.debugManager
    .add('ssao', true)
    .stateChanged.subscribe((dbg) => (renderer.deferredRenderer.ssaoEnabled = dbg.enabled));
  scene.debugManager
    .add('god-rays', true)
    .stateChanged.subscribe(
      (dbg) => (renderer.deferredRenderer.godRaysEnabled = dbg.enabled),
    );
  scene.debugManager
    .add('volumetric', true)
    .stateChanged.subscribe(
      (dbg) => (renderer.deferredRenderer.volumetricEnabled = dbg.enabled),
    );
  scene.debugManager
    .add('fxaa', true)
    .stateChanged.subscribe((dbg) => (renderer.deferredRenderer.fxaaEnabled = dbg.enabled));
}

/**
 * Reconciles the live set of animated render objects with the set of skeleton
 * debug visualizers. Discovers animated objects directly from the scene's
 * object manager, so any object with an AnimationState shows the overlay —
 * regardless of whether physics is active or a BoneCollisionBody is
 * registered. When physics is present and an object happens to have a
 * registered body, its bone capsules are drawn too.
 */
function updateSkeletonOverlay(
  scene: Scene,
  visualizers: Map<RenderObject, SkinnedSkeletonVisualizer>,
): void {
  const physics = scene.features.get('physics') as PhysicsFeature | undefined;
  const bodyByObject = new Map<RenderObject, BoneCollisionBody>();
  if (physics) {
    for (const body of physics.boneHitDetection.targets) {
      bodyByObject.set(body.renderObject, body);
    }
  }

  const live = new Set<RenderObject>();
  for (const obj of scene.objectManager.getAllObjects()) {
    if (!(obj instanceof RenderObject)) continue;
    if (!obj.isAnimated || !obj.animationState || !obj.animation) continue;
    if (!obj.mesh.animationDataTexture) continue;
    live.add(obj);
  }

  // Remove visualizers for objects that are no longer animated / removed.
  for (const [ro, viz] of visualizers) {
    if (!live.has(ro)) {
      scene.objectManager.removeObject(viz);
      viz.destroy();
      visualizers.delete(ro);
    }
  }

  // Add visualizers for newly-discovered animated objects.
  for (const ro of live) {
    if (visualizers.has(ro)) continue;
    const viz = new SkinnedSkeletonVisualizer(scene.ctx, ro, bodyByObject.get(ro));
    visualizers.set(ro, viz);
    scene.objectManager.addObject(viz);
  }
}
